import { useEffect, useMemo, useRef } from "react"

// To make useResource work properly across a hot reload, I need to detect hot reloads. See the comment on useEffect in useResource.
const viteHotApi = import.meta.hot
if (viteHotApi) {
  let data = viteHotApi.data
  if (data.hotReloadCount === undefined) {
    data.hotReloadCount = 0
  }
  viteHotApi.accept((_mod) => {
    data.hotReloadCount += 1
    console.log(`import.meta.hot.accept: ${data.hotReloadCount}`)
  })
}

type UseResourceImpurity = {
  gen: Generator<any, void, never> | undefined,
  isStrictMode: boolean
}

// Based on https://acko.net/blog/react-the-missing-parts/, but with much hacking needed to work with StrictMode and hot reload.
export const useResource = <Resource extends unknown>(
  resourceLifecycle: () => Generator<Resource, void, never>,
  dependencies: React.DependencyList
): Resource => {
  // In StrictMode, React calls useMemo's factory twice in a row and discards the second value. This is normally fine because the factory is supposed to be pure. But here I specifically expect the factory not to be pure. So I need to work around StrictMode.

  // In StrictMode, React also runs each effect (of useEffect) twice in a row (calling the first one's dispose callback in between, if there is one). I need to detect if I'm in strict mode and, if so, skip the first dispose.

  const ref = useRef<UseResourceImpurity>({
    gen: undefined,
    isStrictMode: false
  })

  // These variable are fresh on every call to useResource, but are reused during both calls to useMemo (in StrictMode).
  let workaround: Resource | undefined = undefined

  const resource = useMemo(() => {
    // On the second call in StrictMode, workaround is defined and I just want to return it without creating another resource.
    if (workaround !== undefined) {
      ref.current.isStrictMode = true
      return workaround
    }

    if (ref.current.gen) {
      ref.current.gen.next()
      ref.current.gen = undefined
    }

    const gen = resourceLifecycle()
    ref.current.gen = gen
    const resource = gen.next().value!
    workaround = resource
    return workaround
  }, dependencies)

  // In StrictMode, React runs an effect, then immediately disposes it and runs it again. That's bad for me because I need the resource to survive until the second dispose. There is no second dispose outside of StrictMode.
  let skipDispose = ref.current!.isStrictMode

  // By the Rules of Hooks I have to call useEffect here, even if I know useMemo returned a cached resource.
  // Upon a hot reload (aka hot module replacement or HMR), outside StrictMode, React disposes the old incarnation of this effect after re-rendering components and updating the DOM, and before running the new incarnation of this effect. But the re-render already called this useResource again, destroying the old resource and creating a new one which should NOT be destroyed. So I need to detect a hot reload and not destroy the resource if the effect ran before the latest hot reload.
  const hotReloadCountWhenEffectWasRegistered = import.meta.hot?.data.hotReloadCount ?? 0
  useEffect(() => {
    return () => {
      const hotReloadCountAtDisposeTime = import.meta.hot?.data.hotReloadCount ?? 0

      if (hotReloadCountAtDisposeTime !== hotReloadCountWhenEffectWasRegistered) {
        return
      }

      if (skipDispose) {
        skipDispose = false
      } else {
        ref.current.gen?.next()
        ref.current.gen = undefined
      }
    }
  }, [])

  return resource
}
